if (!customElements.get('chs-bundle-selector')) {
  customElements.define(
    'chs-bundle-selector',
    class BundleSelector extends HTMLElement {
      constructor() {
        super();
        this.options = [];
        this.formId = this.dataset.productFormId;
        this.quantityInput = null;
        this.variantBasePrice = Number(this.dataset.basePrice || 0);
        this.basePrice = Number(this.dataset.basePrice || 0);
        this.totalPriceSelector = this.dataset.totalPriceSelector || '';
        this.totalPriceElement = null;
        this.totalPriceObserver = null;
        this.totalPriceFrame = null;
        this.totalPriceRetry = null;
        this.totalPriceRetryCount = 0;
        this.activeCurrency = window.Shopify?.currency?.active || this.dataset.currencyCode || 'CAD';
        this.shopCurrency = this.dataset.shopCurrency || 'CAD';
        this.savingsBaseCurrency = this.dataset.savingsBaseCurrency || 'CAD';
        this.freeBadgeLabel = this.dataset.freeBadgeLabel || '1 FREE';
        this.moneyFormat = this.dataset.currencyFormat || window.theme?.settings?.money_with_currency_format || '${{amount}}';
        this.onOptionChange = this.onOptionChange.bind(this);
        this.onOptionClick = this.onOptionClick.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
      }

      connectedCallback() {
        this.options = Array.from(this.querySelectorAll('[data-bundle-option]'));
        this.quantityInput = this.getQuantityInput();

        this.options.forEach((option) => {
          const input = option.querySelector('input[type="radio"]');
          if (input) input.addEventListener('change', this.onOptionChange);
        });
        this.addEventListener('click', this.onOptionClick);

        if (this.quantityInput) {
          this.quantityInput.addEventListener('change', this.onQuantityChange);
          this.quantityInput.addEventListener('input', this.onQuantityChange);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.syncFromQuantity());
        }

        this.bindTotalPriceSync();
        this.refreshPrices();
        this.syncFromQuantity();
      }

      disconnectedCallback() {
        this.options.forEach((option) => {
          const input = option.querySelector('input[type="radio"]');
          if (input) input.removeEventListener('change', this.onOptionChange);
        });
        this.removeEventListener('click', this.onOptionClick);

        if (this.quantityInput) {
          this.quantityInput.removeEventListener('change', this.onQuantityChange);
          this.quantityInput.removeEventListener('input', this.onQuantityChange);
        }

        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
        if (this.totalPriceObserver) this.totalPriceObserver.disconnect();
        if (this.totalPriceFrame) cancelAnimationFrame(this.totalPriceFrame);
        if (this.totalPriceRetry) window.clearTimeout(this.totalPriceRetry);
      }

      onOptionChange(event) {
        this.selectQuantity(Number(event.target.value || 1), true);
      }

      onOptionClick(event) {
        const option = event.target.closest('[data-bundle-option]');
        if (!option || !this.contains(option)) return;

        this.selectQuantity(Number(option.dataset.quantity || 1), true);
      }

      onQuantityChange() {
        this.syncFromQuantity();
      }

      onVariantChange(event) {
        if (!event?.data?.variant?.price) return;
        this.variantBasePrice = Number(event.data.variant.price);
        this.basePrice = this.variantBasePrice;
        this.syncBasePriceFromTotal();
        this.refreshPrices();
        this.syncFromQuantity();
      }

      selectQuantity(quantity, updateInput) {
        if (!this.options.length) this.options = Array.from(this.querySelectorAll('[data-bundle-option]'));
        const selectedOption = this.options.find((option) => Number(option.dataset.quantity) === quantity) || this.options[0];
        if (!selectedOption) return;
        this.setActiveOption(selectedOption);

        this.quantityInput = this.quantityInput || this.getQuantityInput();
        if (updateInput && this.quantityInput) {
          this.quantityInput.value = quantity;
          this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      syncFromQuantity() {
        if (!this.options.length) this.options = Array.from(this.querySelectorAll('[data-bundle-option]'));
        if (!this.options.length) return;
        this.quantityInput = this.quantityInput || this.getQuantityInput();
        const quantity = Math.max(1, Number(this.quantityInput?.value || 1));
        const activeOption =
          this.options
            .slice()
            .reverse()
            .find((option) => quantity >= Number(option.dataset.quantity || 1)) || this.options[0];
        this.setActiveOption(activeOption, quantity);
      }

      setActiveOption(activeOption, actualQuantity = null) {
        this.options.forEach((option) => {
          const isActive = option === activeOption;
          option.classList.toggle('is-active', isActive);
          const input = option.querySelector('input[type="radio"]');
          if (input) input.checked = isActive;
        });

        this.updateProperties(activeOption, actualQuantity || Number(activeOption.dataset.quantity || 1));
      }

      updateProperties(option, actualQuantity) {
        this.refreshPrices();
      }

      bindTotalPriceSync() {
        const element = this.getTotalPriceElement();

        if (!element) {
          this.retryTotalPriceSync();
          return;
        }

        this.totalPriceElement = element;
        this.syncBasePriceFromTotal();

        if (typeof MutationObserver !== 'function') return;

        if (this.totalPriceObserver) this.totalPriceObserver.disconnect();

        this.totalPriceObserver = new MutationObserver(() => this.scheduleTotalPriceSync());
        this.totalPriceObserver.observe(element, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      retryTotalPriceSync() {
        if (this.totalPriceRetryCount >= 20) return;

        this.totalPriceRetryCount += 1;
        this.totalPriceRetry = window.setTimeout(() => this.bindTotalPriceSync(), 250);
      }

      scheduleTotalPriceSync() {
        if (this.totalPriceFrame) cancelAnimationFrame(this.totalPriceFrame);

        this.totalPriceFrame = requestAnimationFrame(() => {
          this.totalPriceFrame = null;

          if (this.syncBasePriceFromTotal()) {
            this.refreshPrices();
            this.syncFromQuantity();
          }
        });
      }

      syncBasePriceFromTotal() {
        const element = this.totalPriceElement || this.getTotalPriceElement();
        if (!element) return false;

        const cents = this.extractMoneyCents(element.textContent || element.value || '');
        if (!cents || cents === this.basePrice) return false;

        this.basePrice = cents;
        return true;
      }

      getTotalPriceElement() {
        if (this.totalPriceSelector) {
          try {
            const element = document.querySelector(this.totalPriceSelector);
            if (this.isUsableTotalPriceElement(element)) return element;
          } catch (error) {
            return null;
          }
        }

        return this.findTotalPriceElement();
      }

      findTotalPriceElement() {
        const section = document.getElementById(`MainProduct-${this.dataset.sectionId}`) || this.closest('section') || document;
        const textNodes = document.createTreeWalker(section, NodeFilter.SHOW_TEXT, {
          acceptNode: (node) => {
            if (!/(total\s+stick\s+price|prix\s+total.*b[âa]ton)/i.test(node.nodeValue || '')) return NodeFilter.FILTER_REJECT;
            if (node.parentElement?.closest?.('chs-bundle-selector')) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        });

        let labelNode = textNodes.nextNode();

        while (labelNode) {
          let container = labelNode.parentElement;

          for (let depth = 0; container && depth < 5; depth += 1) {
            const priceElement = this.findMoneyElementInContainer(container);
            if (priceElement) return priceElement;
            container = container.parentElement;
          }

          labelNode = textNodes.nextNode();
        }

        return null;
      }

      findMoneyElementInContainer(container) {
        if (!container || container.closest?.('chs-bundle-selector')) return null;

        const elements = Array.from(container.querySelectorAll('*')).filter((element) => this.isUsableTotalPriceElement(element));
        return elements[elements.length - 1] || null;
      }

      isUsableTotalPriceElement(element) {
        if (!element || element.closest?.('chs-bundle-selector')) return false;

        const text = element.textContent || element.value || '';
        return this.extractMoneyCents(text) > 0;
      }

      extractMoneyCents(text) {
        const matches = String(text || '').match(/(?:CA\$|US\$|\$)?\s*\d[\d,]*(?:\.\d{2})?/g);
        if (!matches) return null;

        const raw = matches[matches.length - 1].replace(/[^0-9.]/g, '');
        const value = Number(raw);

        return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : null;
      }

      getQuantityInput() {
        const sectionId = this.dataset.sectionId;
        const formId = this.formId;
        const escapedFormId = this.escapeSelector(formId);
        const selectors = [
          `#Quantity-${sectionId}`,
          `#Quantity-Form-${sectionId} input[name="quantity"]`,
          escapedFormId ? `input[name="quantity"][form="${escapedFormId}"]` : '',
          escapedFormId ? `form#${escapedFormId} input[name="quantity"]` : '',
        ].filter(Boolean);

        for (const selector of selectors) {
          const input = document.querySelector(selector);
          if (input) return input;
        }

        const nextQuantityBlock = this.parentElement?.querySelector?.('.product-form__quantity input[name="quantity"]');
        if (nextQuantityBlock) return nextQuantityBlock;

        const section = document.getElementById(`MainProduct-${sectionId}`) || this.closest('section');
        return section?.querySelector?.('input[name="quantity"]') || null;
      }

      escapeSelector(value) {
        if (!value) return '';
        if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
        return String(value).replace(/["\\]/g, '\\$&');
      }

      refreshPrices() {
        this.options.forEach((option) => {
          const quantity = Number(option.dataset.quantity || 1);
          const isFreeItem = option.hasAttribute('data-free-item');
          const savingsValue = isFreeItem
            ? this.basePrice / 100
            : this.convertSavingsValue(Number(option.dataset.savings || 0));
          const original = this.basePrice * quantity;
          const total = this.calculateTotal(quantity, quantity, savingsValue);
          const priceEl = option.querySelector('[data-bundle-price]');
          const compareEl = option.querySelector('[data-bundle-compare]');
          const badgeEl = option.querySelector('[data-bundle-badge]');

          if (priceEl) priceEl.textContent = this.formatMoney(total);
          if (compareEl) compareEl.textContent = this.formatMoney(original);
          if (badgeEl) badgeEl.textContent = isFreeItem ? this.freeBadgeLabel : this.formatSavings(savingsValue);
        });
      }

      calculateTotal(actualQuantity, bundleQuantity, savingsValue) {
        const original = this.basePrice * actualQuantity;
        if (bundleQuantity <= 1 || savingsValue <= 0) return original;

        const savingsAmount = Math.round(savingsValue * 100);

        return Math.max(0, original - savingsAmount);
      }

      convertSavingsValue(savingsValue) {
        if (!savingsValue || this.activeCurrency === this.savingsBaseCurrency) return savingsValue;

        const rate = this.getCurrencyRate();
        return rate ? savingsValue * rate : savingsValue;
      }

      getCurrencyRate() {
        const shopifyCurrency = window.Shopify?.currency;
        const active = shopifyCurrency?.active || this.activeCurrency;
        const rate = Number(shopifyCurrency?.rate || 0);

        if (active === this.activeCurrency && this.shopCurrency === this.savingsBaseCurrency && Number.isFinite(rate) && rate > 0) {
          return rate;
        }

        return 0;
      }

      formatSavings(savingsValue) {
        return `-${this.formatCompactMoney(Math.round(savingsValue * 100))}`;
      }

      formatCompactMoney(cents) {
        return this.formatMoney(cents)
          .replace(/\.00(?=\D|$)/, '')
          .replace(/\s?(CAD|USD)$/i, '');
      }

      formatMoney(cents) {
        if ((this.activeCurrency === 'CAD' || this.activeCurrency === 'USD') && window.Intl) {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: this.activeCurrency,
            currencyDisplay: 'narrowSymbol',
          }).format(Number(cents || 0) / 100);
        }

        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, this.moneyFormat);
        }

        if (typeof formatMoney === 'function') {
          return formatMoney(cents, this.moneyFormat);
        }

        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }

    }
  );
}
